<template>
  <Toast />
  <Dialog
    v-if="authStore.showRevokedModal"
    :visible="authStore.showRevokedModal"
    :closable="false"
    :dismissable-mask="false"
    modal
    :header="t('session.revoked')"
    :style="{ width: '420px' }"
  >
    <p data-cy="revoked-message">{{ sessionRevokedMessage }}</p>
    <template #footer>
      <Button
        :label="t('session.sign_in_again')"
        @click="authStore.confirmRevokedSession()"
        data-cy="revoked-sign-in-again"
        autofocus
      />
    </template>
  </Dialog>

  <Teleport to="body">
    <div
      v-if="navigationLoaderStore.loading"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <ProgressSpinner
        :pt="{
          circle: '!stroke-primary',
        }"
      />
    </div>
    <div
      v-if="navigationLoaderStore.slowConnection"
      class="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 rounded-lg bg-white px-4 py-2 shadow-md"
      role="status"
    >
      <i class="pi pi-spin pi-spinner" />
      <span class="text-sm">{{ t("connection_error.slow_connection") }}</span>
    </div>
  </Teleport>

  <div class="flex not-print:h-screen overflow-hidden print:overflow-visible">
    <div
      v-if="authStore.is.loggedIn() && !authStore.is.passcode() && !hideSidebar"
      :class="[
        'lg:block shrink-0 transition-[width] duration-300 print:hidden',
        sidebarCollapsed ? 'w-0 overflow-hidden ' : 'w-62',
      ]"
    >
      <AppSidebar @toggle="sidebarCollapsed = true" />
    </div>
    <Button
      v-if="sidebarCollapsed && authStore.is.loggedIn() && !authStore.is.passcode() && !hideSidebar"
      icon="pi pi-chevron-right"
      size="small"
      variant="outlined"
      severity="secondary"
      class="!absolute left-[-25px] top-2 !rounded-md !bg-white !px-8 !pr-6 !justify-start"
      v-tooltip.left="'Show menu'"
      @click="sidebarCollapsed = false"
    />

    <div class="flex-1 flex flex-col overflow-hidden print:overflow-visible" id="app-main-wrapper">
      <main
        :class="[
          { 'px-12 py-4': !isMobile },
          'flex-1 overflow-y-auto print:overflow-visible print:!p-0',
        ]"
      >
        <div class="flex flex-col mb-4 print:hidden" v-if="!hideSidebar">
          <div id="gph-breadcrumbs"></div>
          <div class="flex items-center h-[49px] gap-2">
            <Button
              :key="$route.fullPath"
              v-if="pageHeaderStore.goTo"
              data-cy="page-back"
              size="small"
              class="shrink-0"
              icon="pi pi-arrow-left"
              rounded
              outlined
              @click="pageHeaderStore.goTo"
              v-tooltip.left="'Back'"
            />
            <Skeleton v-if="showLoadingSkeleton" width="20rem" />
            <!-- v-show, not v-else: #gph-title is a Teleport target and must never be re-created -->
            <h1
              v-show="!showLoadingSkeleton"
              :class="[{ invisible: hidePageHeader }, '!text-3xl shrink-0']"
            >
              <span id="gph-title">
                <span class="gph-default">{{ pageHeader }}</span>
              </span>
            </h1>

            <div class="flex-1 flex justify-end items-center gap-2">
              <Transition name="slide-fade-forward">
                <div
                  class="flex items-center gap-2"
                  v-if="
                    authStore.is.staff() &&
                    organizationConfigStore.test.isTesting &&
                    organizationConfigStore.currentId
                  "
                >
                  <small class="!text-warning">Test features applied</small>
                  <router-link
                    :to="{
                      name: 'entity-settings',
                      params: { id: organizationConfigStore.currentId },
                    }"
                  >
                    <Button
                      size="small"
                      icon="pi pi-flag"
                      severity="info"
                      variant="outlined"
                      v-tooltip.bottom="'View the applied test features'"
                    />
                  </router-link>
                </div>
              </Transition>
              <div id="gph-actions" class="flex items-center gap-2"></div>
            </div>
          </div>
        </div>
        <RouterView v-slot="{ Component, route }">
          <Transition
            v-if="route.meta?.transitionName"
            :name="route.meta?.transitionName as string"
            mode="in-out"
            appear
          >
            <component :is="Component"></component>
          </Transition>
          <component :is="Component" v-else></component>
        </RouterView>
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { RouterView, useRoute } from "vue-router";
import { Toast, ProgressSpinner, Button, Skeleton, Dialog } from "primevue";
import { useToast } from "primevue/usetoast";
import { useToastStore } from "@/stores/toast";
import { useI18n } from "vue-i18n";
import { computed, onMounted, ref, watch } from "vue";
import { useNavigationLoaderStore } from "@/stores/navigationLoader";
import { useAuthStore } from "./stores/auth";
import AppSidebar from "@/components/AppSidebar.vue";
import { useOrganizationConfig } from "./stores/organizationConfig";
import { usePageHeaderStore } from "@/stores/pageHeader";
import { useActivityTracker } from "@/hooks/useActivityTracker";

const $route = useRoute();
const navigationLoaderStore = useNavigationLoaderStore();
const toastStore = useToastStore();
const authStore = useAuthStore();
const organizationConfigStore = useOrganizationConfig();
const pageHeaderStore = usePageHeaderStore();
const toast = useToast();
const { t, te } = useI18n();
useActivityTracker();

const sidebarCollapsed = ref(localStorage.getItem("sidebarCollapsed") === "true");

watch(sidebarCollapsed, (val) => {
  localStorage.setItem("sidebarCollapsed", String(val));
});

const sessionRevokedMessage = computed(() => {
  const key = authStore.revokedReason;
  // A reason without its own message (e.g. one added in functions only) falls back to the generic one
  if (key && te(`session.${key}`)) return t(`session.${key}`);
  return t("session.revoked");
});

const isMobile = ref(false);
onMounted(() => {
  const obs = new MutationObserver((mut) => {
    for (const m of mut) {
      if (m.target.nodeName == "BODY") {
        isMobile.value = document.body.classList.contains("mobile");
      }
    }
  });
  obs.observe(document.body, { attributes: true });
});
const hideSidebar = computed(() => !!$route.meta.hideSidebar || isMobile.value);
const pageHeader = computed(() => {
  const h = $route.meta.pageHeader;
  if (typeof h === "string") {
    return h;
  }
  let name = "";
  if (typeof $route.name === "string" && $route.name.length) {
    name = $route.name[0].toUpperCase() + $route.name.slice(1, $route.name.length);
  }
  return name;
});

const showLoadingSkeleton = ref(false);
const hidePageHeader = ref(false);
let loadingTimeoutId: ReturnType<typeof setTimeout> | undefined;
watch(
  () => pageHeaderStore.loading,
  (loading) => {
    if (loadingTimeoutId) {
      clearTimeout(loadingTimeoutId);
      loadingTimeoutId = undefined;
    }
    if (loading) {
      hidePageHeader.value = true;
      loadingTimeoutId = setTimeout(() => {
        hidePageHeader.value = false;
        showLoadingSkeleton.value = true;
      }, 500);
    } else {
      showLoadingSkeleton.value = false;
      hidePageHeader.value = false;
    }
  },
);

watch(
  () => toastStore.toast,
  (newToast) => {
    if (newToast) {
      toast.add(newToast);
    }
  },
);
</script>

<style>
#gph-title:has(> .gph-custom) > .gph-default {
  display: none;
}
</style>
